import { toLiteral } from "../../helper/locale-utils";
import { getFeatureConfigurationIsEnabled } from "../../helper/feature-configuration-helper";
import store from "../../redux/store";
import allFields from "./data/formFields";

export interface IField {
  id: string;
  label: string;
  labelSmall: string;
  name: string;
  position: string;
  tooltip?: string;
  infoTooltip?: {
    title?: string;
    content?: string;
  };
  type?: string;
  visible?: boolean;
  disabled?: boolean;
  disableRelated?: string[];
  parentField?: string;
  requireRelated?: string[];
  valueRelated?: string[];
  placeholder?: string;
  validations?: string[] | never[];
  options?: SelectFieldOption[];
  multiple?: boolean;
  isSimple?: boolean; // Text save how option (Select)
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  value?: string;
  customFieldProps?: any;
  configuration?: boolean;
  blockForUsers?: boolean;
  notDisabledForSuperAdmin?: boolean;
  emptyOption?: boolean;
  customOnChange?: string;
  defaultValue?: number | string;
  customValidations?: string[];
  readonly?: boolean;
  showCVCheckboxInputForOrderType?: string;
  isByDefaultChecked?: boolean;
  nonMandatoryFieldsOnSelection?: {
    selectedValue: string;
    nonMandatoryFields: string[];
  };
  updateRelatedFields?: any;
}

export interface SelectFieldOption {
  label: string;
  value: string;
  dependentValue?: string;
}

export interface ITenantFields {
  tenantId: string;
  fields: IField[] | never | undefined;
}

export const getOptionFieldLabel = (fieldName: string, optionValue: string): string => {
  const _field = getTenantField(fieldName);
  if (!_field) return optionValue;
  const _option = _field?.options?.find((_opt) => _opt?.value === optionValue);
  if (!_option) return optionValue;
  return toLiteral({ id: _option?.label });
};

export const getTenantField = (name: string): IField | undefined => {
  let _field: IField | undefined = undefined;
  const fields = getTenantFields();
  _field = fields?.find((field) => field.name === name);
  return _field;
};

const filterRequiredValidation = (tenantField: any) => {
  const newValidations = tenantField?.validations?.filter((tenantValidation: any) => tenantValidation !== "required") ?? [];
  return { ...tenantField, validations: newValidations };
};

const removeRequiredValidation = (nonMandatoryFields: any = [], selectedTenantFields: IField[] | undefined = undefined) => {
  return selectedTenantFields?.map((tenantField: any) => {
    if (nonMandatoryFields.includes(tenantField.name)) {
      return filterRequiredValidation(tenantField);
    } else if (tenantField?.customFieldProps) {
      let customTenantFields = tenantField.customFieldProps;
      for (const key in customTenantFields) {
        if (nonMandatoryFields.includes(customTenantFields[key]?.name)) {
          customTenantFields[key] = filterRequiredValidation(customTenantFields[key]);
        }
      }
      tenantField.customFieldProps = customTenantFields;
      return tenantField;
    }
    return tenantField;
  });
};

export const getTenantFields = (position: string = "allFields", fieldValues: any = {}, isCheckingForMandatoryFields: boolean = false): IField[] | undefined => {
  let tenantID = store.getState().tenant.selectedTenant.alias;
  const { sourcing_jd_fields } = store.getState().tenant.configurations;
  const _allTenants: any = allFields;
  let _selectedTenant: ITenantFields | undefined = undefined;
  let _displayedFields: IField[] | undefined = undefined;
  if (tenantID) {
    _selectedTenant = _allTenants && _allTenants.find((a: ITenantFields) => a.tenantId === tenantID);
    const sourcingJDFromDatabaseEnabled = getFeatureConfigurationIsEnabled("Sourcing Settings", "jd-fields-from-database");
    if (sourcingJDFromDatabaseEnabled) _selectedTenant = { tenantId: tenantID, fields: structuredClone(sourcing_jd_fields) };
    //if there isn't fields for this tenant select default
    _selectedTenant = _selectedTenant ? _selectedTenant : _allTenants.find((a: ITenantFields) => a.tenantId === "default");
    if (_selectedTenant) {
      if (isCheckingForMandatoryFields && _selectedTenant?.fields) {
        const fieldWhichMakeOtherFieldsOptional = _selectedTenant?.fields?.find((tenantField: any) => tenantField?.nonMandatoryFieldsOnSelection) as any;
        const fieldValue = fieldValues?.[fieldWhichMakeOtherFieldsOptional?.name];
        const { selectedValue = "", nonMandatoryFields = [] } = fieldWhichMakeOtherFieldsOptional?.nonMandatoryFieldsOnSelection ?? {};
        if (nonMandatoryFields?.length && selectedValue === fieldValue) {
          _selectedTenant.fields = removeRequiredValidation(nonMandatoryFields, _selectedTenant.fields);
        }
      }
      if (position !== "allFields") {
        _displayedFields = _selectedTenant?.fields && _selectedTenant?.fields?.filter((a) => a.position === position);
      } else {
        _displayedFields = _selectedTenant?.fields ? _selectedTenant?.fields : _displayedFields;
      }
    }
  }
  return _displayedFields;
};

export const getJobDataComplete = (jobDataFields: any) => {
  const leftValues = getTenantFields("step-1-left");
  const rightValues = getTenantFields("step-1-right");
  let requiredValues: any[] = leftValues ? leftValues.filter((leftValue) => leftValue.visible !== false && leftValue.validations && (leftValue.validations as string[]).includes("required")) : [];
  requiredValues = requiredValues.concat(
    rightValues ? rightValues.filter((rightValue) => rightValue.visible !== false && rightValue.validations && (rightValue.validations as string[]).includes("required")) : []
  );
  requiredValues = requiredValues.map((requiredValue: any) => requiredValue.name);

  for (const value of requiredValues) {
    if (value === "dataApplyURL") {
      const valueField = jobDataFields.dataCustomFields.find((field: any) => field.key === value);
      if (!valueField || !valueField.value) return false;
    } else {
      if (!jobDataFields[`${value}`]) return false;
    }
  }
  return true;
};
export const getJobDescriptionComplete = (jobDescriptionFields: any) => {
  const leftValues = getTenantFields("step-2-left");
  let requiredValues: any[] = leftValues ? leftValues.filter((leftValue) => leftValue.visible !== false && leftValue.validations && (leftValue.validations as string[]).includes("required")) : [];
  requiredValues = requiredValues.map((requiredValue: any) => requiredValue.name);
  for (const value of requiredValues) {
    if (!jobDescriptionFields[`${value}`]) {
      return false;
    }
  }
  return true;
};
